/** Durée mini d'un pas de case : un segment à 0 ms pose la voiture d'un coup. */
export const LIVE_CAR_CELL_MS = 140;

export type MotionEase = 'easeOutCubic' | 'linear';

export interface MotionPose {
  x: number;
  z: number;
  rotY: number;
}

export interface MotionSegment {
  to: MotionPose;
  durationMs: number;
  ease?: MotionEase;
}

export interface CarMotion {
  from: MotionPose;
  to: MotionPose;
  startedAt: number;
  durationMs: number;
  ease?: MotionEase;
  queue: MotionSegment[];
}

function easeOutCubic(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 3);
}

function applyEase(t: number, ease: MotionEase = 'easeOutCubic'): number {
  const clamped = Math.min(1, Math.max(0, t));
  return ease === 'linear' ? clamped : easeOutCubic(clamped);
}

function lerpAngle(from: number, to: number, t: number): number {
  const twoPi = Math.PI * 2;
  let delta = (to - from) % twoPi;
  if (delta > Math.PI) delta -= twoPi;
  if (delta < -Math.PI) delta += twoPi;
  return from + delta * t;
}

function angleDelta(a: number, b: number): number {
  const twoPi = Math.PI * 2;
  let d = Math.abs(a - b) % twoPi;
  if (d > Math.PI) d = twoPi - d;
  return d;
}

export function posesClose(a: MotionPose, b: MotionPose): boolean {
  return Math.hypot(a.x - b.x, a.z - b.z) <= 0.02 && angleDelta(a.rotY, b.rotY) < 0.02;
}

export function motionFinalPose(motion: CarMotion): MotionPose {
  const last = motion.queue[motion.queue.length - 1];
  return last ? last.to : motion.to;
}

function positiveDuration(durationMs: number): number {
  return durationMs > 0 ? durationMs : LIVE_CAR_CELL_MS;
}

export type EnqueueMotionOptions = {
  ease?: MotionEase;
};

/**
 * Ajoute une case à la file. Ne remplace pas le segment en cours : sinon la
 * voiture saute à la fin du segment précédent dès qu'une case suivante arrive.
 */
export function enqueueMotion(
  current: CarMotion | null,
  visual: MotionPose,
  target: MotionPose,
  durationMs: number,
  now: number,
  options?: EnqueueMotionOptions
): CarMotion | null {
  const dur = positiveDuration(durationMs);
  const ease = options?.ease ?? 'easeOutCubic';
  if (current) {
    if (posesClose(motionFinalPose(current), target)) return current;
    return {
      ...current,
      queue: [...current.queue, { to: target, durationMs: dur, ease }],
    };
  }
  if (posesClose(visual, target)) return null;
  return {
    from: visual,
    to: target,
    startedAt: now,
    durationMs: dur,
    ease,
    queue: [],
  };
}

/**
 * Avance d'au plus un segment par échantillon : un trou de frame ne doit pas
 * avaler plusieurs cases d'un coup.
 */
export function sampleMotion(
  motion: CarMotion,
  now: number
): { pose: MotionPose; motion: CarMotion | null } {
  const dur = motion.durationMs;
  const t = dur > 0 ? Math.min(1, (now - motion.startedAt) / dur) : 1;
  const e = applyEase(t, motion.ease);
  const pose: MotionPose = {
    x: motion.from.x + (motion.to.x - motion.from.x) * e,
    z: motion.from.z + (motion.to.z - motion.from.z) * e,
    rotY: lerpAngle(motion.from.rotY, motion.to.rotY, e),
  };
  if (t < 1) return { pose, motion };
  const [next, ...rest] = motion.queue;
  if (!next) return { pose: motion.to, motion: null };
  return {
    pose: motion.to,
    motion: {
      from: motion.to,
      to: next.to,
      startedAt: now,
      durationMs: next.durationMs,
      ease: next.ease ?? motion.ease,
      queue: rest,
    },
  };
}
